import Modal from '@/components/Modal'
import { Box, Typography } from '@mui/material'

type Props = {
  open: boolean
  onClose: () => void
}

const TermsAndConditionsModal = ({ open, onClose }: Props) => {
  return (
    <Modal open={open} onClose={onClose} maxWidth="90%">
      <Box p={3} data-testid="terms-modal">
        <Typography variant="h2" fontSize={24} borderBottom={1} pb={2}>
          Affiliate Terms and Conditions
        </Typography>
        <Typography mt={2}>KEY TERMS</Typography>
        <ul>
          <li>
            Standard Referral Fees: 8% commission on all sales that you refer
            using your affiliate link.
          </li>
          <li>
            Commission is deposited into your PayPal account after accruing over
            $500 in affiliate income.
          </li>
          <li>You must have a PayPal account in order to receive payment.</li>
          <li>
            You must have a website, blog, Twitter or Facebook account to place
            your affiliate link(s).
          </li>
          <li>
            Sales are tracked using session cookies (the default browser option
            for most users).
          </li>
          <li>
            Unethical use or abuse of this program may result in membership
            termination.
          </li>
          <li>
            Your affiliate ID may NOT be used for your own purchases. Doing so
            may result in membership termination.
          </li>
          <li>
            We reserve the right to modify these terms at any time (with written
            notification).
          </li>
        </ul>
        <Typography>PLEASE READ THIS AGREEMENT IN ITS ENTIRETY.</Typography>
        <Typography>
          BY JOINING OUR AFFILIATE PROGRAM, YOU AGREE TO THE FOLLOWING TERMS AND
          CONDITIONS (THE "AGREEMENT") GOVERNING YOUR USE OF THE STACKCOMMERCE
          AFFILIATE PROGRAM (THE "PROGRAM"). IF YOU ARE ENTERING INTO THIS
          AGREEMENT ON BEHALF OF A COMPANY OROTHER LEGAL ENTITY, YOU REPRESENT
          THAT YOU HAVE THE RIGHT, POWER AND AUTHORITY TO ENTER INTO THIS
          AGREEMENT AND TO BIND SUCH ENTITY TO THIS AGREEMENT.
        </Typography>
        <ol>
          <li>
            Links/graphics/advertisements on your site, in your emails, or other
            communications
          </li>
          <Typography>
            Once you have signed up for the Affiliate Program, you will be
            assigned a unique Partner Link (which may take the form of an actual
            link and/or promotion code). You may not provide the Partner Link to
            any third party without express prior written consent from
            StackCommerce. Affiliate may develop graphics, emails, and other
            promotional materials, but must obtain express prior written
            approval from StackCommerce prior to usage. All uses of the Partner
            Link will include attribution to StackCommerce in accordance with
            StackCommerce’s branding guidelines, which may be provided to you or
            posted on our website. StackCommerce may provide templates and
            approved graphics, banners, and other materials via its website or
            via StackCommerce’s application programming interface (API), which
            you can be assigned a unique API key in order to enable access –
            this is provided upon request or by applying on the StackCommerce
            website if online application capability is available at that time
            for API keys. StackCommerce may also provide you with custom
            creative units to place on your web site(s), at StackCommerce’s sole
            discretion. When a customer clicks on a Partner Link a tracking
            cookie (“Cookie”) will be placed on that customer’s computer. A user
            can only have one StackCommerce Cookie at a time, so if another
            approved affiliate sends a customer to StackCommerce, the Cookie
            will be updated and the most recent affiliate to make the customer
            referral via the Partner Link will receive credit.
          </Typography>
          <li>Referral fees and payment</li>
          <Typography>
            Qualifying Purchases & Payment: We will pay a referral fee in
            connection with “Qualifying Purchases”, which (subject to the
            exclusions) occur when:
          </Typography>
          <ol type="a">
            <li>a customer clicks-through the Partner Link; and</li>
            <li>
              during a single session, purchases one or more StackCommerce
              Products from StackCommerce on StackCommerce.com, or one of
              StackCommerce’s merchant clients on StackCommerce.com
              (collectively, “Products”), within seven days of the original
              Cookie,
            </li>
            <li>
              the StackCommerce Product is shipped to, claimed or downloaded by,
              and paid for by the customer.
            </li>
          </ol>
          <Typography>
            Only transactions that are captured by StackCommerce are eligible
            for a referral fee. Unless otherwise agreed upon, Affiliate will
            receive a revenue share of eight percent (8%) of StackCommerce’s
            Gross Revenue for each bona fide customer sale that is tracked and
            reported by our systems through the Partner Link (“Fees”). Payments
            begin once you've earned more than five hundred dollars ($500) in
            affiliate income. As long as your current affiliate earnings are
            over five hundred dollars ($500), payments will be made within sixty
            (60) days after the end of the calendar month in which they accrue.
            However, if you haven't earned five hundred dollars ($500) since
            your last payment, we'll pay you during the next payment cycle after
            you've crossed the five hundred dollars ($500) threshold. A third
            party payment processor may be used by StackCommerce to process
            payments. In some instances, the third party will require additional
            personal identifying information to comply with applicable federal
            and state laws. Fees for transferring funds may be deducted from
            earnings to Affiliates. Fees may vary by payment method and payment
            amount, and may be changed at any time.
          </Typography>
          <Typography>
            Disqualified Purchases: Notwithstanding the foregoing, Qualifying
            Purchases can be disqualified whenever they occur in connection with
            a violation of the Affiliate Program. StackCommerce reserves the
            right to disqualify referral fees earned through fraudulent,
            illegal, or overly aggressive, questionable sales or marketing
            methods.
          </Typography>
          <Typography>
            Further, the following purchases that would otherwise be Qualified
            Purchases are disqualified and excluded from the Program:
          </Typography>
          <ol type="a">
            <li>any Product purchased after termination of your Agreement,</li>
            <li>
              any Product order where a cancellation, return or refund has been
              initiated,
            </li>
            <li>
              unless otherwise agreed upon, any Product purchased by a customer
              who is referred to a StackCommerce powered shop or site through
              any advertisement that you purchased through participation in
              bidding or auction on keywords, search terms, or other identifiers
              that include the word or variations or misspellings of
              “stacksocial”, “citizen goods”, “joyus”, “skillwise” or any other
              trademark owned by StackCommerce (all, a “Prohibited Paid Search
              Placement”),
            </li>
            <li>
              any Product purchased by a customer who is referred by a link that
              is generated or displayed on a search engine (including Google,
              Yahoo, Bing, or any other search portal, sponsored advertising
              service, or other search or referral service, or any site that
              participates in such search engine’s network) (a “Search Engine”),
            </li>
            <li>
              any Product purchased by a customer, where such customer does not
              comply with the standard Terms of Use on shops,
            </li>
            <li>
              any Product purchase that is not correctly tracked or reported
              because the links from your site are not properly formatted,
            </li>
            <li>
              any Product purchased through cookie stuffing techniques that set
              the tracking cookie without the potential customer actually
              clicking on the Partner Link
            </li>
            <li>
              any Product purchased by a customer who shares the same
              personal/company details as the Affiliate (a “Self-Referral”).
            </li>
          </ol>
          <li>Reporting</li>
          <Typography>
            StackCommerce will provide an online dashboard where Partner can
            review earnings. Upon request, StackCommerce will provide Affiliate
            a report within fifteen (15) days after the end of each calendar
            month that will include number of referral customers who made
            purchases along with a commercially reasonable breakdown of the
            transactions.
          </Typography>
          <li>Customer definition</li>
          <Typography>
            Customers who buy products through this Program will be deemed to be
            our customers. Accordingly, all of our rules, policies, and
            operating procedures concerning customer orders, customer service,
            and product sales will apply.
          </Typography>
          <li>Your responsibilities</li>
          <Typography>
            You will be solely responsible for the development, operation, and
            maintenance of your site and for all materials that appear on your
            site. You are responsible for all content posted and activity that
            occurs under your account. You may not use the Affiliate Program for
            any illegal or unauthorized purpose.
          </Typography>
          <li>No Spam</li>
          <Typography>
            As a condition of your participation in the Program you will comply
            with all applicable laws (federal, state or otherwise) that govern
            marketing email, including without limitation, the CAN-SPAM Act of
            2003 and all other anti-spam laws.
          </Typography>
          <li>Term of the agreement and Program</li>
          <Typography>
            The term of this agreement will begin when Affiliate clicks Accept
            or signs this agreement in hard copy and will end when terminated by
            either party. Either party may terminate this agreement at any time
            without cause by giving the other party thirty (30) days prior
            written notice of termination. Either party may terminate this
            agreement at any time for cause by giving the other party prior
            written notice of termination. Upon the termination of this
            agreement, you will immediately cease use of, and remove from your
            site, all links and other materials provided by or on behalf of us
            to you pursuant hereto or in connection with the Program.
            StackCommerce reserves the right to end the Program at any time.
            Upon termination, StackCommerce will pay any outstanding earnings
            accrued above per the terms defined above. Sections 9, 10, 11, 12,
            13, 14, and 16 shall survive any termination of this agreement.
          </Typography>
          <li>Relationship of Parties</li>
          <Typography>
            Affiliate and StackCommerce are independent contractors, and nothing
            in this agreement will create any partnership, joint venture,
            agency, franchise, sales representative, or employment relationship
            between the parties. Affiliate will have no authority to make or
            accept any offers or representations on our behalf.
          </Typography>
          <li>Confidentiality</li>
          <Typography>
            During the term of this agreement, each party (a “Disclosing Party”)
            may provide the other party (a “Receiving Party”) with confidential
            and/or proprietary materials and information (“Confidential
            Information”). All materials and information provided by Disclosing
            Party to Receiving Party and identified at the time of disclosure as
            “Confidential” or bearing a similar legend, and all other
            information that the Receiving Party reasonably should have known
            was the Confidential Information of the Disclosing Party, shall be
            considered Confidential Information. Receiving Party shall maintain
            the confidentiality of the Confidential Information and will not
            disclose such information to any third party without the prior
            written consent of Disclosing Party. Receiving Party will only use
            the Confidential Information internally for the purposes
            contemplated hereunder. The obligations in this Section 9 shall not
            apply to any information that (i) is made generally available to the
            public without breach of this agreement, (ii) is developed by the
            Receiving Party independently from the Confidential Information,
            (iii) is disclosed to Receiving Party by a third party without
            restriction, or (iv) was in the Receiving Party’s lawful possession
            prior to the disclosure and was not obtained by the Receiving Party
            either directly or indirectly from the Disclosing Party. Receiving
            Party may disclose Confidential Information as required by law or
            court order; provided that, Receiving Party provides Disclosing
            Party with prompt written notice thereof and uses its best efforts
            to limit disclosure. At any time, upon Disclosing Party’s request,
            Receiving Party shall return to Disclosing Party all Disclosing
            Party’s Confidential Information in its possession, including,
            without limitation, all copies and extracts thereof. Notwithstanding
            the foregoing, Receiving Party may disclose Confidential Information
            to any third-party to the limited extent necessary to exercise its
            rights, or perform its obligations, under this Agreement; provided
            that all such third parties are bound in writing by obligations of
            confidentiality and non-use at least as protective of the Disclosing
            Party’s Confidential Information as this agreement. The parties
            acknowledge that the harm caused by the wrongful disclosure of
            Confidential Information will be difficult, if not impossible, to
            assess on a monetary basis alone, and that legal damages may not be
            sufficient compensation for such wrongful disclosure. Therefore,
            either party may enforce its rights under this Section by equitable
            means, including, but not limited to, injunctive relief, in addition
            to any other remedies to which it is otherwise entitled.
          </Typography>
          <li>Indemnity</li>
          <Typography>
            You shall defend, indemnify, and hold harmless StackCommerce, its
            affiliates, and each of its and its affiliates’ employees,
            contractors, directors, suppliers, and representatives from all
            liabilities, damages, losses, claims, and expenses, including
            reasonable attorneys' fees, that arise (i) from your breach of this
            agreement, (ii) from any claim that our use of the Affiliate
            trademarks infringes on any trademark, trade name, service mark,
            copyright, license intellectual property or other proprietary right
            of any third party, (iii) from any claim related to your site,
            emails or other communications, including, without limitation,
            content therein not attributable to us, or (iv) otherwise from your
            use of the Program. StackCommerce reserves the right to assume the
            exclusive defense and control of any matter otherwise subject to
            indemnification by you, in which event you will assist and cooperate
            with StackCommerce in asserting any available defenses.
            StackCommerce will indemnify, defend, or at its option settle and
            hold you harmless from all liabilities, damages, losses, claims, and
            expenses, including reasonable attorneys' fees, brought by a third
            party, that arise: (a) from death of or injury to any person or
            persons or out of any damage to property resulting from or in
            connection with a Product; or (b) from any act or omission by
            StackCommerce in connection with StackCommerce’s provision of the
            Product.
          </Typography>
          <li>Limitations of Liability</li>
          <Typography>
            IN NO EVENT SHALL StackCommerce BE LIABLE UNDER CONTRACT, TORT,
            STRICT LIABILITY, NEGLIGENCE OR ANY OTHER LEGAL OR EQUITABLE THEORY
            UNDER THIS AGREEMENT (I) FOR ANY LOST PROFITS, DATA LOSS, COST OF
            PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES, OR SPECIAL, INDIRECT,
            INCIDENTAL, PUNITIVE, OR CONSEQUENTIAL DAMAGES OF ANY KIND
            WHATSOEVER (HOWEVER ARISING) OR (II) FOR ANY DAMAGES IN EXCESS OF
            (IN THE AGGREGATE) THE TOTAL FEES PAID TO AFFILIATE IN THE TWELVE
            MONTHS PRECEDING THE EVENT THAT GAVE RISE TO THE CLAIM.
          </Typography>
          <li>Disclaimers</li>
          <Typography>
            NEITHER StackCommerce NOR ANY OF ITS CLIENT OR SUPPLIERS MAKES ANY
            EXPRESS OR IMPLIED WARRANTIES OF ANY KIND WITH RESPECT TO THE
            PROGRAM OR ANY PRODUCTS SOLD THROUGH THE PROGRAM, INCLUDING, WITHOUT
            LIMITATION, WARRANTIES OF TITLE, NON-INFRINGEMENT, MERCHANTABILITY
            AND FITNESS FOR A PARTICULAR PURPOSE OR ANY IMPLIED WARRANTIES
            ARISING OUT OF A COURSE OF PERFORMANCE, DEALING OR TRADE USAGE, ALL
            OF WHICH ARE HEREBY EXPRESSLY DISCLAIMED. IN ADDITION, StackCommerce
            MAKES NO REPRESENTATION THAT THE OPERATION OF THE PRODUCT SITES WILL
            BE UNINTERRUPTED OR ERROR-FREE, AND StackCommerce WILL NOT BE LIABLE
            FOR THE CONSEQUENCES OF ANY INTERRUPTIONS OR ERRORS.
          </Typography>
          <li>Non-Interference</li>
          <Typography>
            Affiliate agrees that during the period of this agreement and for a
            period of one hundred and eighty (180) days thereafter, Affiliate
            will not solicit or enter into any relationship with then current
            clients, customers or merchants (including parties which
            StackCommerce is engaged in business discussions) of StackCommerce
            relating, directly or indirectly, to the Program.
          </Typography>
          <li>Arbitration</li>
          <Typography>
            Any dispute relating in any way to this agreement (including any
            actual or alleged breach hereof), any transactions or activities
            under this agreement or your relationship with us or any of our
            affiliates shall be submitted to confidential arbitration in San
            Francisco, CA, USA. Arbitration under this agreement shall be
            conducted under the rules then prevailing of the Judicial
            Arbitration and Mediation Services, Inc. (“JAMS”), and you agree to
            submit to such arbitration. The arbitrator's award shall be binding
            and may be entered as a judgment in any court of competent
            jurisdiction.
          </Typography>
          <li>Modifications to the Terms</li>
          <Typography>
            StackCommerce reserves the right, at its sole discretion, to modify
            or replace any of this agreement, or change, suspend, or discontinue
            the Program (including without limitation the payment of Fees) at
            any time by posting a notice on its website or by sending you an
            email. StackCommerce may also impose limits on Affiliate’s
            participation in the Program without notice or liability. It is your
            responsibility to check StackCommerce’s affiliate page of it website
            periodically for changes. Your continued participation in the
            Program following the posting of any changes to this agreement
            constitutes acceptance of those changes.
          </Typography>
          <li>Miscellaneous</li>
          <Typography>
            This agreement will be governed by the laws of the state of
            California, USA, without reference to rules governing choice of
            laws. You may not assign this agreement, by operation of law or
            otherwise, without our prior written consent. StackCommerce may
            freely assign this agreement. Subject to that restriction, this
            agreement will be binding on, inure to the benefit of, and be
            enforceable against the parties and their respective successors and
            assigns. Our failure to enforce your strict performance of any
            provision of this agreement will not constitute a waiver of our
            right to subsequently enforce such provision or any other provision
            of this agreement. The agreement constitutes the entire agreement
            between you and StackCommerce and governs your participation in the
            Affiliate Program, superseding any prior agreements between you and
            StackCommerce (including, but not limited to, any prior versions of
            the agreement).
          </Typography>
        </ol>
      </Box>
    </Modal>
  )
}

export default TermsAndConditionsModal
